import { Add, ContentPaste, Delete } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationNamespace } from 'i18n';
import { BidirectionalMapping } from 'types';

type Props = {
  value: BidirectionalMapping | undefined;
  onChange: (value: BidirectionalMapping) => void;
  internalLabel?: string;
  providerLabel?: string;
  disabled?: boolean;
};

type MappingRow = {
  internal: string;
  provider: string;
};

const KEYS = {
  internal_value:
    'pages.dynamic_adapter_configs.dialog.mappings.internal_value',
  provider_value:
    'pages.dynamic_adapter_configs.dialog.mappings.provider_value',
  actions: 'pages.dynamic_adapter_configs.dialog.mappings.actions',
  add_mapping: 'pages.dynamic_adapter_configs.dialog.mappings.add_mapping',
  no_mappings: 'pages.dynamic_adapter_configs.dialog.mappings.no_mappings',
  paste_json: 'pages.dynamic_adapter_configs.dialog.mappings.paste_json',
  paste_json_apply:
    'pages.dynamic_adapter_configs.dialog.mappings.paste_json_apply',
  paste_json_error:
    'pages.dynamic_adapter_configs.dialog.mappings.paste_json_error',
} as const;

// Convert value prop to local rows (only complete mappings)
const valueToRows = (value: BidirectionalMapping | undefined): MappingRow[] => {
  if (!value?.toProvider) {
    return [];
  }
  return Object.entries(value.toProvider).map(([internal, provider]) => ({
    internal,
    provider: provider as string,
  }));
};

export const BidirectionalMappingTable: React.FC<Props> = ({
  value,
  onChange,
  internalLabel,
  providerLabel,
  disabled = false,
}) => {
  const { t: tAdmin } = useTranslation(TranslationNamespace.Admin);
  const t = tAdmin as unknown as (key: string) => string;
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  // Local state tracks all rows including incomplete ones
  const [localRows, setLocalRows] = useState<MappingRow[]>(() =>
    valueToRows(value),
  );
  const [showJsonInput, setShowJsonInput] = useState(false);
  const [jsonInputValue, setJsonInputValue] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Sync local rows when value changes externally (e.g., initial load, reset)
  useEffect(() => {
    const valueRows = valueToRows(value);
    setLocalRows((currentRows) => {
      // Only update if the complete rows have changed (avoid overwriting user edits)
      const currentComplete = currentRows.filter(
        (r) => r.internal && r.provider,
      );
      if (JSON.stringify(currentComplete) !== JSON.stringify(valueRows)) {
        return valueRows;
      }
      return currentRows;
    });
  }, [value]);

  // Propagate only complete rows to parent
  const propagateToParent = useCallback(
    (rows: MappingRow[]) => {
      const toProvider: Record<string, string> = {};
      const toInternal: Record<string, string> = {};

      rows.forEach((row) => {
        if (row.internal && row.provider) {
          toProvider[row.internal] = row.provider;
          toInternal[row.provider] = row.internal;
        }
      });

      onChange({ toProvider, toInternal });
    },
    [onChange],
  );

  const handleAddRow = useCallback(() => {
    setLocalRows((prev) => [...prev, { internal: '', provider: '' }]);
  }, []);

  const handleRemoveRow = useCallback(
    (index: number) => {
      const newRows = localRows.filter((_, i) => i !== index);
      setLocalRows(newRows);
      propagateToParent(newRows);
    },
    [localRows, propagateToParent],
  );

  const handleChangeRow = useCallback(
    (index: number, field: keyof MappingRow, newValue: string) => {
      const newRows = localRows.map((row, i) =>
        i === index ? { ...row, [field]: newValue } : row,
      );
      setLocalRows(newRows);
      propagateToParent(newRows);
    },
    [localRows, propagateToParent],
  );

  const handleJsonApply = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonInputValue);

      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        setJsonError(t(KEYS.paste_json_error));
        return;
      }

      const hasInvalidValues = Object.values(parsed).some(
        (v) => typeof v !== 'string',
      );
      if (hasInvalidValues) {
        setJsonError(t(KEYS.paste_json_error));
        return;
      }

      const pastedRows: MappingRow[] = Object.entries(parsed).map(
        ([internal, provider]) => ({
          internal,
          provider: provider as string,
        }),
      );

      // Merge: overwrite existing keys, append new
      const existingMap = new Map(
        localRows
          .filter((r) => r.internal && r.provider)
          .map((r) => [r.internal, r]),
      );
      pastedRows.forEach((row) => existingMap.set(row.internal, row));
      const mergedRows = Array.from(existingMap.values());

      setLocalRows(mergedRows);
      propagateToParent(mergedRows);
      setShowJsonInput(false);
      setJsonInputValue('');
      setJsonError(null);
    } catch {
      setJsonError(t(KEYS.paste_json_error));
    }
  }, [jsonInputValue, localRows, propagateToParent, t]);

  const handleJsonCancel = useCallback(() => {
    setShowJsonInput(false);
    setJsonInputValue('');
    setJsonError(null);
  }, []);

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{internalLabel || t(KEYS.internal_value)}</TableCell>
            <TableCell>{providerLabel || t(KEYS.provider_value)}</TableCell>
            <TableCell width={80} align="center">
              {t(KEYS.actions)}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {localRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} align="center">
                <Typography variant="body2" color="text.secondary">
                  {t(KEYS.no_mappings)}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            localRows.map((row, index) => (
              <TableRow key={index}>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.internal}
                    onChange={(e) =>
                      handleChangeRow(index, 'internal', e.target.value)
                    }
                    disabled={disabled}
                    placeholder="card"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.provider}
                    onChange={(e) =>
                      handleChangeRow(index, 'provider', e.target.value)
                    }
                    disabled={disabled}
                    placeholder="credit_card"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveRow(index)}
                    disabled={disabled}
                    color="error"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={handleAddRow}
          disabled={disabled}
        >
          {t(KEYS.add_mapping)}
        </Button>
        <Button
          size="small"
          startIcon={<ContentPaste />}
          onClick={() => setShowJsonInput(true)}
          disabled={disabled || showJsonInput}
        >
          {t(KEYS.paste_json)}
        </Button>
      </Stack>

      <Collapse in={showJsonInput}>
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={jsonInputValue}
            onChange={(e) => {
              setJsonInputValue(e.target.value);
              setJsonError(null);
            }}
            placeholder='{"card": "credit_card", "sbp": "SBP"}'
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
            }}
          />
          {jsonError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {jsonError}
            </Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" variant="contained" onClick={handleJsonApply}>
              {t(KEYS.paste_json_apply)}
            </Button>
            <Button size="small" onClick={handleJsonCancel}>
              {tCommon('buttons.cancel')}
            </Button>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};
